import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosRequest from '@/api';
import { CampaignProductData } from '@/@types/CampaignProduct';
import * as AD from './index.styled';
import { ResData } from '@/@types';
import { Form, Input, Button, Select, Typography, Modal, message, Upload, Alert, TablePaginationConfig } from 'antd';
import { ExclamationCircleOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';
import type { RcFile } from 'antd/es/upload/interface';
import { CampaignProductPromotionCreateData, CampaignProductPromotionData, CampaignProductPromotionResponse } from '@/@types/CampaignProductPromotion';
import DataTable from '@/components/@common/DataTable';
import { AxiosError } from 'axios';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { confirm } = Modal;

const statusOptions = [
    { label: 'Active', value: 'a' },
    { label: 'Pending', value: 'p' },
    { label: 'Deactivated', value: 'd' },
];

const categoryOptions = [
    { label: '대행사', value: 'a' },
    { label: '고객사', value: 'c' },
];

const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

function CampaignProductDetail() {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState<number>(0);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [campaign_product, setCampaignProduct] = useState<CampaignProductData | null>(null);
    const [campaignProductPromotionList, setCampaignProductPromotionList] = useState<CampaignProductPromotionData[]>([]);
    const [uploading, setUploading] = useState(false);
    const [showStatusAlert, setShowStatusAlert] = useState(false);
    const [showPartnerAlert, setShowPartnerAlert] = useState(false);
    const [form] = Form.useForm();
    const [newData, setNewData] = useState<CampaignProductPromotionCreateData>({
        campaign_product_id: id ? Number(id) : 0,
        promotion_point: 0,
        promotion_percent: 0,
        promotion_reward_point: 0,
        start_date: '',
        end_date: '',
    });
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [promotionPoint, setPromotionPoint] = useState<number | null>(null);
    const [promotionPercent, setPromotionPercent] = useState<number | null>(null);
    const [promotionRewardPoint, setPromotionRewardPoint] = useState<number | null>(null);


    useEffect(() => {
        fetchCampaignProduct();
        fetchCampaignProductPromotionData();
    }, [id, currentPage, pageSize]);

    const fetchCampaignProduct = async () => {
        try {
            const response = await axiosRequest.requestAxios<ResData<CampaignProductData>>('get', `/campaign_product/${id}`);
            setCampaignProduct(response.data);
            console.log(response.data);
        } catch (error) {
            console.error('Error fetching campaign product:', error);
        }
    };

    const fetchCampaignProductPromotionData = async () => {
        try {
            const params = new URLSearchParams();
            params.append('page', currentPage.toString());
            params.append('size', pageSize.toString());
            const response = await axiosRequest.requestAxios<ResData<CampaignProductPromotionResponse>>('get', `/campaign_product/${id}/promotions?${params.toString()}`);
            if (response.data && response.data.result) {
                const formattedData = response.data.result.map((item, index) => ({
                    ...item,
                    number: (currentPage - 1) * pageSize + index + 1,

                }));
                setCampaignProductPromotionList(formattedData);
                setTotalCount(response.data.total_count);
            } else {
                console.error('No data returned from the API');
            }
            console.log(response.data);
        } catch (error) {
            console.error('Error fetching campaign product:', error);
        }
    };

    const columns = [
        {
            title: 'No',
            dataIndex: 'number',
            key: 'number',
            width: 20,
        },
        {
            title: '프로모션 광고단가',
            dataIndex: 'promotion_point',
            key: 'promotion_point',
            width: 120,
            render: (promotion_point: string | null) => promotion_point ? promotion_point : '-'
        },
        {
            title: '프로모션 고객적립율',
            dataIndex: 'promotion_percent',
            key: 'promotion_percent',
            width: 130,
            render: (promotion_percent: number | null) => promotion_percent ? `${promotion_percent}%` : '-'
        },
        {
            title: '프로모션 기간',
            dataIndex: 'duration_str',
            key: 'duration_str',
            render: (duration_str: string | null) => duration_str ? duration_str : '-'
        },
    ];

    const handlePaginationChange = (pagination: TablePaginationConfig) => {
        setCurrentPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 10);
    };

    const handleSave = async (values: any) => {
        const formData = {
            campaign_product_id: id,
            ...values
        }
        formData.start_date = startDate;
        formData.end_date = endDate;
        console.log(formData)
        try {

            const response = await axiosRequest.requestAxios<ResData<CampaignProductPromotionResponse>>(
                'post',
                '/campaign_product_promotion',
                formData,
            );
            if (response) {
                message.success('프로모션이 추가됐습니다.');
                fetchCampaignProductPromotionData();
            }
        } catch (error) {
            const axiosError = error as AxiosError;
            if (axiosError.response && axiosError.response.data) {
                const detailMessage = (axiosError.response.data as any).detail;
                if (detailMessage === 'Another promotion exists in the specified duration') {
                    alert('해당 기간에 중복된 프로모션이 존재합니다.');
                } else {
                    message.error('프로모션 등록에 실패했습니다.');
                }
            }
        }

    }

    const handleStartDateChange = (date: Dayjs | null) => {
        setStartDate(date ? date.format('YYYY-MM-DD') : ''); // 시작 날짜 상태를 업데이트합니다.
        setNewData(prevData => ({
            ...prevData,
            start_date: date ? date.format('YYYY-MM-DD') : '',
        }));
    };

    const handleEndDateChange = (date: Dayjs | null) => {
        setEndDate(date ? date.format('YYYY-MM-DD') : ''); // 종료 날짜 상태를 업데이트합니다.
        setNewData(prevData => ({
            ...prevData,
            end_date: date ? date.format('YYYY-MM-DD') : '',
        }));
    };

    // 시작 날짜는 오늘이거나 이후
    const disabledStartDate = (current: Dayjs) => {
        return current.isBefore(dayjs().startOf('day'));
    };

    // 종료 날짜는 시작 날짜 이후
    const disabledEndDate = (current: Dayjs) => {
        return startDate ? current.isBefore(startDate, 'day') : false;
    };



    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setNewData(prevData => ({
            ...prevData,
            [name]: value,
        }));
        if (name === 'promotion_point') {
            setPromotionPoint(value ? parseFloat(value) : 0);
        } else if (name === 'promotion_percent') {
            setPromotionPercent(value ? parseFloat(value) : 0);
        }
    };

    useEffect(() => {
        if (promotionPoint !== null && promotionPercent !== null) {
            const rewardPoint = (promotionPoint * promotionPercent) / 100;
            setPromotionRewardPoint(rewardPoint);
            form.setFieldsValue({ promotion_reward_point: rewardPoint.toFixed(2) });
        }
    }, [promotionPoint, promotionPercent, form]);



    return (
        <AD.Wrapper>
            <Title level={2}>상품 수정</Title>
            <AD.StyledForm
                form={form}
                layout="vertical"
                initialValues={{
                    ...campaign_product,
                }}
                onFinish={handleSave}
            >
                {/* <AD.StyledDivider>기본정보</AD.StyledDivider> */}
                <table>
                    <tbody>
                        <tr>
                            <td>상품명</td>
                            <td>

                                <span>{campaign_product?.name ? campaign_product?.name : ''}</span>

                            </td>
                        </tr>

                        <tr>
                            <td>광고단가 (vat별도)</td>
                            <td>

                                <span>{campaign_product?.basic_point ? campaign_product?.basic_point : 0} </span>

                            </td>
                        </tr>
                    </tbody>
                </table>
                <br />
                <table>
                    <tr>
                        <td>프로모션 기간</td>
                        <td>
                            <AD.DatePickerBox>
                                <AD.StyledFormItem
                                    name="start_date"
                                    rules={[{ required: true, message: '시작 날짜를 선택해주세요.' }]}
                                >
                                    <AD.StyledDatePicker
                                        name="start_date"
                                        onChange={handleStartDateChange}
                                        placeholder="시작 날짜 선택"
                                        disabledDate={disabledStartDate}
                                    />
                                </AD.StyledFormItem>
                                <AD.StyledFormItem
                                    name="end_date"
                                    rules={[{ required: true, message: '종료 날짜를 선택해주세요.' }]}
                                >
                                    <AD.StyledDatePicker
                                        name="end_date"
                                        onChange={handleEndDateChange}
                                        placeholder="종료 날짜 선택"
                                        disabledDate={disabledEndDate}
                                    />
                                </AD.StyledFormItem>
                            </AD.DatePickerBox>
                        </td>
                    </tr>
                    <tr>
                        <td>프로모션 광고단가</td>
                        <td>

                            <AD.StyledFormItem
                                name="promotion_point"
                                rules={[
                                    { required: true, message: '프로모션 기본가를 입력하세요' },
                                    { pattern: /^[0-9]+$/, message: '숫자만 입력해 주세요.' },
                                ]}
                            >
                                <AD.InputField
                                    name="promotion_point"
                                    onChange={handleInputChange}
                                    type="number"
                                    placeholder="프로모션 기본가"
                                />
                            </AD.StyledFormItem>
                        </td>
                    </tr>
                    <tr>
                        <td>프로모션 고객 적립율</td>
                        <td>
                            <AD.StyledFormItem
                                name="promotion_percent"
                                rules={[
                                    { required: true, message: '프로모션 적립율을 입력하세요' },
                                    { pattern: /^[0-9]+$/, message: '숫자만 입력해 주세요.' },
                                ]}
                            >
                                <AD.InputField
                                    name="promotion_percent"
                                    onChange={handleInputChange}
                                    type="number"
                                    placeholder="프로모션 적립율 %"
                                />
                            </AD.StyledFormItem>


                            <AD.StyledFormItem
                                name="promotion_reward_point"
                                rules={[
                                    { required: true, message: '프로모션 적립포인트를 입력하세요' },
                                ]}
                            >
                                <AD.InputField
                                    disabled
                                    value={promotionRewardPoint !== null ? promotionRewardPoint.toFixed(2) : 0}
                                    name="promotion_reward_point"
                                    type="number"
                                    placeholder="프로모션 적립포인트"
                                />
                            </AD.StyledFormItem>
                        </td>
                    </tr>
                </table>
                <AD.BtnContainer>
                    <Button type="primary" htmlType="submit" style={{ width: '80px' }}>
                        등록하기
                    </Button>
                </AD.BtnContainer>

                {/* <AD.StyledDivider>내역</AD.StyledDivider> */}
                <>
                    <DataTable
                        data={campaignProductPromotionList}
                        totalCount={totalCount}
                        columns={columns}
                        onChange={handlePaginationChange}
                        tableTitle="내역"
                    />
                </>


            </AD.StyledForm>
        </AD.Wrapper>
    );
}

export default CampaignProductDetail;
